import React, { useMemo, useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

// Components
import PropertyCard from "@/components/property/PropertyCard";
const PropertyPreview = lazy(() => import("@/components/property/PropertyPreview").then(module => ({ default: module.PropertyPreview })));

// Hooks & Types
import { useProperties } from "@/hooks/useProperties";
import type { Property } from "@/hooks/useProperties";

import { PropertyCardSkeleton } from "@/components/property/PropertyCardSkeleton";

const FeaturedProperties: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { properties, loading } = useProperties(i18n.language);

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // ✅ Dernières 3 propriétés (plus logique que slice(0,3) si API renvoie tri asc)
  const featuredProperties = useMemo(() => {
    if (!properties || properties.length === 0) return [];
    return [...properties].slice(0, 3);
    // Si tu veux vraiment les "dernières" :
    // return [...properties].slice(-3).reverse();
  }, [properties]);

  const handlePreview = (property: Property) => {
    setSelectedProperty(property);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setSelectedProperty(null);
  };

  return (
    <section className="relative py-28 sm:py-36 lg:py-44 px-6 sm:px-8 lg:px-12 overflow-hidden bg-[#F8F5EF]">
      <div className="max-w-7xl mx-auto">
        {/* HEADER SECTION */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="mb-16 sm:mb-20"
        >
          <div className="flex items-center gap-5 mb-12 max-w-xl">
            <span className="font-oswald text-[11px] sm:text-xs uppercase tracking-[0.34em] text-[#86523d] whitespace-nowrap">
              {t("featured.eyebrow", "Sélection Exclusive")}
            </span>
            <span className="h-px flex-1 bg-stone-300/80" aria-hidden="true" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
            <h2 className="lg:col-span-8 font-oswald font-medium uppercase tracking-[-0.035em] text-[clamp(2.7rem,6vw,5.5rem)] text-stone-900 leading-[0.96]">
              {t("featured.title", "Dernières Opportunités")}
            </h2>

            <p className="lg:col-span-4 lg:pb-2 font-playfair text-lg sm:text-xl leading-relaxed text-stone-600">
              {t(
                "featured.subtitle",
                "Une collection de propriétés d'exception sélectionnées pour leur caractère unique."
              )}
            </p>
          </div>
        </motion.div>

        {/* GRID SECTION */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-8">
            {[1, 2, 3].map((i) => (
              <PropertyCardSkeleton key={i} />
            ))}
          </div>
        ) : featuredProperties.length > 0 ? (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-10"
          >
            {featuredProperties.map((property, index) => (
              <motion.div
                key={property.cod_ofer}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: index * 0.18,
                  ease: [0.22, 1, 0.36, 1],
                }}
                viewport={{ once: true }}
              >
                <PropertyCard property={property} onPreview={handlePreview} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-20">
            <p className="font-playfair italic text-stone-400 text-lg">
              {t("featured.empty", "Aucune propriété disponible pour le moment.")}
            </p>
          </div>
        )}

        {/* BOTTOM CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 sm:mt-20 flex justify-center"
        >
          <Link
            to="/properties"
            className="group inline-flex items-center gap-4 px-8 py-4 border border-stone-300 hover:border-[#a26850] text-stone-700 hover:text-[#86523d] font-oswald text-[10px] tracking-[0.26em] uppercase transition-colors duration-300"
          >
            {t("featured.cta", "Voir tout le catalogue")}
            <span className="text-sm transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
          </Link>
        </motion.div>
      </div>

      {/* MODAL PREVIEW */}
      <AnimatePresence>
        {isPreviewOpen && selectedProperty && (
          <Suspense fallback={null}><PropertyPreview
            property={selectedProperty}
            isOpen={isPreviewOpen}
            onClose={closePreview}
            lang={i18n.language}
          /></Suspense>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FeaturedProperties;
